import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Inbox } from 'lucide-react';
import { Tooltip } from '@mui/material';
import GenericTable from '../../../../components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from '../../../../components/table/GenericTable.design';
import { CHAMADOS_CONFIG_DESIGN } from './ChamadosConfig.design';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import {
  actionCriarCaixaEntrada,
  actionDeletarCaixaEntrada,
} from 'src/actions/chamados/actionChamadosConfig';
import { Badge } from 'src/@/components/ui/badge';
import { fDate } from 'src/utils/formatTime';
import Iconify from 'src/components/Iconify';

const TABLE_HEAD = [
  { id: 'nome', label: 'Nome', className: 'flex-1' },
  { id: 'descricao', label: 'Descrição', className: 'flex-1' },
  { id: 'status', label: 'Status', className: 'w-28 text-center' },
  { id: 'publicado', label: 'Publicado', className: 'w-28 text-center' },
  { id: 'visibilidade', label: 'Visibilidade', className: 'w-36 text-center' },
  { id: 'criado_em', label: 'Criado em', className: 'w-32 text-center' },
  { id: 'actions', label: 'Ações', className: 'w-24 text-right' },
];

export default function CaixasEntradaTab({ listaCaixas, onRefresh, clienteId }) {
  const navigate = useNavigate();
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [loading, setLoading] = useState(false);
  const [caixaId, setCaixaId] = useState('');

  const totais = {
    total: listaCaixas.length,
    visiveis: listaCaixas.filter(c => c.visibilidade_cliente).length,
    ativas: listaCaixas.filter(c => c.ativo).length,
    publicadas: listaCaixas.filter(c => c.isPublished).length,
  };

  async function handleCriar() {
    try {
      setLoading(true);
      const response = await actionCriarCaixaEntrada({ nome: 'Nova Caixa de Entrada', cliente_id: clienteId });
      const novaCaixa = response.data.data;
      navigate(`/dashboard/configuracoes/chamados-config-builder/${novaCaixa._id}`);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeletar() {
    try {
      setLoading(true);
      await actionDeletarCaixaEntrada(caixaId);
      await onRefresh();
      setOpenConfirmacao(false);
      setCaixaId('');
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Action button */}
      <div className="flex justify-end">
        <button
          onClick={handleCriar}
          disabled={loading}
          className={CHAMADOS_CONFIG_DESIGN.page.actionButton}
        >
          <Plus size={18} />
          <span>Criar Caixa</span>
        </button>
      </div>

      {/* Modals */}
      <ModalConfimacao
        open={openConfirmacao}
        onClose={() => setOpenConfirmacao(false)}
        onConfirm={handleDeletar}
        loadingFinalizar={loading}
        mensagem="Deseja mesmo excluir essa caixa de entrada?"
      />

      {/* Table */}
      {listaCaixas.length === 0 ? (
        <div className={CHAMADOS_CONFIG_DESIGN.emptyState.container}>
          <div className={CHAMADOS_CONFIG_DESIGN.emptyState.iconBox}>
            <Inbox size={32} />
          </div>
          <h3 className={CHAMADOS_CONFIG_DESIGN.emptyState.title}>Nenhuma caixa de entrada</h3>
          <p className={CHAMADOS_CONFIG_DESIGN.emptyState.description}>
            Crie caixas de entrada para organizar chamados externos.
          </p>
          <button
            onClick={handleCriar}
            disabled={loading}
            className={CHAMADOS_CONFIG_DESIGN.emptyState.button}
          >
            Criar minha primeira caixa
          </button>
        </div>
      ) : (
        <GenericTable headLabels={TABLE_HEAD}>
          {listaCaixas.map((row) => (
            <div
              key={row._id}
              className={`${GENERIC_TABLE_DESIGN.row.container} cursor-pointer`}
              onClick={() => navigate(`/dashboard/configuracoes/chamados-config-builder/${row._id}`)}
            >
              {/* Nome */}
              <div className="flex-1 min-w-[200px] pr-4">
                <div className="flex items-center gap-3">
                  <div className={GENERIC_TABLE_DESIGN.common.iconBox}>
                    <Inbox size={20} />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{row.nome}</span>
                </div>
              </div>

              {/* Descrição */}
              <div className="flex-1 pr-4">
                <span className="text-sm text-gray-500 line-clamp-1">
                  {row.descricao || '—'}
                </span>
              </div>

              {/* Status */}
              <div className="w-28 text-center flex justify-center">
                {row.status === 'active' ? (
                  <Badge variant="outline" className="text-xs font-medium flex items-center gap-1.5 bg-green-50 text-green-700 border-green-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    Ativo
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs font-medium flex items-center gap-1.5 bg-amber-50 text-amber-700 border-amber-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Rascunho
                  </Badge>
                )}
              </div>

              {/* Publicado */}
              <div className="w-28 text-center flex justify-center">
                {row.isPublished ? (
                  <Badge variant="outline" className="text-xs font-medium flex items-center gap-1.5 bg-blue-50 text-blue-700 border-blue-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    Sim
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs font-medium flex items-center gap-1.5 bg-gray-50 text-gray-600 border-gray-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    Não
                  </Badge>
                )}
              </div>

              {/* Visibilidade */}
              <div className="w-36 text-center flex justify-center">
                {row.visibilidade_cliente ? (
                  <Badge variant="outline" className="text-xs font-medium flex items-center gap-1.5 bg-green-50 text-green-700 border-green-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    Visível
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs font-medium flex items-center gap-1.5 bg-gray-50 text-gray-600 border-gray-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    Interno
                  </Badge>
                )}
              </div>

              {/* Criado em */}
              <div className="w-32 text-center">
                <span className="text-xs text-gray-400">{fDate(row.criado_em)}</span>
              </div>

              {/* Actions */}
              <div className="w-24 flex justify-end gap-1">
                <Tooltip title="Excluir">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCaixaId(row._id);
                      setOpenConfirmacao(true);
                    }}
                    className={GENERIC_TABLE_DESIGN.common.deleteButton}
                  >
                    <Trash2 size={18} />
                  </button>
                </Tooltip>
              </div>
            </div>
          ))}
        </GenericTable>
      )}
    </div>
  );
}
